import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { legacy, rhm, apiError, money, dt, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Card, Empty, Modal, PageHead, Select, Table, Td, statusTone } from "../ui";

const STATUSES = ["pending", "placed", "confirmed", "processing", "packed", "dispatched", "delivered", "fulfilled", "cancelled", "returned", "refunded"];

const EMAIL_LABELS = {
    "order-confirmation": "Order confirmation",
    "order-status": "Status update",
    "order-alert": "Owner alert",
    "review-request": "Review request",
    "cart-1": "Cart reminder (4h)",
    "cart-2": "Cart reminder (24h)",
    welcome: "Welcome",
};

/** Which emails this order has triggered — sent, skipped or failed. */
const OrderEmailLog = ({ orderNumber }) => {
    const { data: rows = [], isLoading } = useQuery({
        queryKey: ["rhm-order-emails", orderNumber],
        queryFn: async () => (await rhm.get(`/orders/${orderNumber}/emails`)).data,
        enabled: !!orderNumber,
    });
    return (
        <div data-testid="rhm-order-emails">
            <div className="mono text-[10px] tracking-[0.18em] text-gold mb-2">EMAILS SENT</div>
            {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : rows.length ? (
                <div className="space-y-1.5">
                    {rows.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-3 border-b border-white/5 py-2"
                            data-testid={`rhm-order-email-${r.id}`}>
                            <span className="text-zinc-300">
                                {EMAIL_LABELS[r.kind] || r.kind}
                                <span className="text-zinc-500 text-xs"> · {r.to}</span>
                            </span>
                            <span className="flex items-center gap-2 shrink-0">
                                <span className="text-xs text-zinc-500">{dt(r.created_at)}</span>
                                <Badge tone={r.status === "sent" ? "good" : r.status === "failed" ? "danger" : "muted"}>
                                    {(r.status || "").toUpperCase()}
                                </Badge>
                            </span>
                        </div>
                    ))}
                </div>
            ) : <div className="text-zinc-500 text-xs">No emails recorded for this order yet.</div>}
        </div>
    );
};

export default function RhmOrders() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [detail, setDetail] = useState(null);

    const { data: orders = [], isLoading } = useQuery({
        queryKey: ["rhm-orders"],
        queryFn: async () => (await legacy.get("/orders")).data,
    });

    const [payFilter, setPayFilter] = useState("all");
    const shown = orders.filter((o) => payFilter === "all"
        || (payFilter === "paid" && o.payment_status === "paid")
        || (payFilter === "pending" && !["paid"].includes(o.payment_status) && o.payment_status !== "failed")
        || (payFilter === "failed" && o.payment_status === "failed"));

    const setStatus = useMutation({
        mutationFn: async ({ orderNumber, status }) => (await legacy.patch(`/orders/${orderNumber}/status`, { status })).data,
        onSuccess: () => {
            toast.success("Order status updated");
            qc.invalidateQueries({ queryKey: ["rhm-orders"] });
            qc.invalidateQueries({ queryKey: ["rhm-dashboard"] });
        },
        onError: (e) => toast.error(apiError(e)),
    });

    return (
        <div data-testid="rhm-orders-page">
            <PageHead title="Orders" sub="Website orders with fulfilment status." />

            <Card title={`${shown.length} order(s)`}
                action={<Select value={payFilter} className="!w-44 !py-1.5" data-testid="rhm-orders-payment-filter"
                    onChange={(e) => setPayFilter(e.target.value)}>
                    <option value="all">All payments</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Payment pending</option>
                    <option value="failed">Payment failed</option>
                </Select>}>
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-orders-table" head={["Order", "Customer", "Items", "Total", "Payment", "Status", "Placed", ""]}
                        empty={!shown.length && <Empty msg="No orders yet." />}>
                        {shown.map((o) => (
                            <tr key={o.order_number} data-testid={`rhm-order-row-${o.order_number}`}>
                                <Td className="mono text-xs text-gold">{o.order_number}</Td>
                                <Td>
                                    <div className="font-semibold">{o.address?.full_name}</div>
                                    <div className="text-xs text-zinc-500">{o.address?.city} · {o.address?.phone}</div>
                                </Td>
                                <Td className="text-sm text-zinc-400">
                                    {o.items?.map((i) => `${i.product_name} × ${i.quantity}`).join(", ")}
                                </Td>
                                <Td>{money(o.total)}</Td>
                                <Td className="mono text-[10px]">
                                    <Badge tone={o.payment_status === "paid" ? "good"
                                        : o.payment_status === "failed" ? "danger" : "warn"}>
                                        {(o.payment_status || "pending").toUpperCase()}
                                    </Badge>
                                    <div className="text-zinc-500 mt-1">{(o.payment_method || "").toUpperCase()}</div>
                                </Td>
                                <Td>
                                    {can("orders.edit") && !(o.payment_status !== "paid" && o.payment_method === "cashfree") ? (
                                        <Select value={o.status} className="!w-36 !py-1.5"
                                            data-testid={`rhm-order-status-${o.order_number}`}
                                            onChange={(e) => setStatus.mutate({ orderNumber: o.order_number, status: e.target.value })}>
                                            {[...new Set([o.status, ...STATUSES])].map((s) => (
                                                <option key={s} value={s}>{titleCase(s)}</option>
                                            ))}
                                        </Select>
                                    ) : (
                                        <div>
                                            <Badge tone={statusTone(o.status)}>{titleCase(o.status)}</Badge>
                                            {o.payment_status !== "paid" && o.payment_method === "cashfree" && (
                                                <div className="mono text-[9px] text-zinc-500 mt-1"
                                                    data-testid={`rhm-order-locked-${o.order_number}`}>
                                                    AWAITING PAYMENT
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Td>
                                <Td className="text-xs text-zinc-500 whitespace-nowrap">{dt(o.created_at)}</Td>
                                <Td>
                                    <button onClick={() => setDetail(o)} data-testid={`rhm-order-view-${o.order_number}`}
                                        className="mono text-[10px] tracking-[0.16em] text-gold">VIEW</button>
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Modal open={!!detail} onClose={() => setDetail(null)} title={`Order ${detail?.order_number || ""}`} testId="rhm-order-detail">
                {detail && (
                    <div className="space-y-5 text-sm">
                        <div>
                            <div className="mono text-[10px] tracking-[0.18em] text-gold mb-2">ITEMS</div>
                            {detail.items?.map((i, idx) => (
                                <div key={idx} className="flex justify-between border-b border-white/5 py-2">
                                    <span>{i.product_name} × {i.quantity}</span>
                                    <span>{money(i.subtotal)}</span>
                                </div>
                            ))}
                            <div className="flex justify-between py-2 text-zinc-400"><span>Subtotal</span><span>{money(detail.subtotal)}</span></div>
                            <div className="flex justify-between py-1 text-zinc-400"><span>Shipping</span><span>{money(detail.shipping)}</span></div>
                            <div className="flex justify-between py-1 text-zinc-400"><span>Tax</span><span>{money(detail.tax)}</span></div>
                            <div className="flex justify-between py-2 font-bold text-gold"><span>Total</span><span>{money(detail.total)}</span></div>
                        </div>
                        <div>
                            <div className="mono text-[10px] tracking-[0.18em] text-gold mb-2">DELIVERY</div>
                            <div className="text-zinc-300">
                                {detail.address?.full_name}<br />
                                {detail.address?.address_line1}{detail.address?.address_line2 ? `, ${detail.address.address_line2}` : ""}<br />
                                {detail.address?.city}, {detail.address?.state} {detail.address?.pincode}<br />
                                {detail.address?.phone} · {detail.address?.email}
                            </div>
                        </div>
                        {detail.notes && (
                            <div>
                                <div className="mono text-[10px] tracking-[0.18em] text-gold mb-2">NOTES</div>
                                <div className="text-zinc-300">{detail.notes}</div>
                            </div>
                        )}
                        <OrderEmailLog orderNumber={detail.order_number} />
                    </div>
                )}
            </Modal>
        </div>
    );
}
